/**
 * adminVehicleSlice — Redux Toolkit slice for admin Vehicle write
 * operations + bulk-action UX state (CON-VEH-001).
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   selectedIds: number[];
 *   bulkReassignOpen: boolean;
 *   mutationStatus: 'idle' | 'pending' | 'success' | 'error';
 *   mutationError: ApiError | null;
 * }
 * ```
 *
 * Why a slice and not just TanStack Query
 * ---------------------------------------
 * The list page itself is a TanStack Query consumer (server-side
 * pagination + cache invalidation under the `vehicle-list` query key).
 * The slice only tracks the UI state that crosses query/route boundaries:
 *
 *  - `selectedIds` — checked rows in the DataTable. Lives here so the
 *    bulk-action bar can reach for them without the table re-rendering
 *    its toolbar on every parent prop change.
 *  - `bulkReassignOpen` — whether the bulk reassign-type dialog is open.
 *  - `mutationStatus / mutationError` — last write attempt state for
 *    toasts + form-level error banners. The DTO of the freshly-saved
 *    vehicle does NOT live here; the container patches the TanStack Query
 *    cache directly so React Query stays the source of truth for fetched
 *    data.
 *
 * The four `createAsyncThunk` thunks (create / update / delete /
 * bulk-update) wrap `@dfq/shared/api/adminVehicles` so `dispatch(...)`
 * call sites in the container can `await` them and surface failures via
 * the unwrapped `ApiError` payload.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only the page-level containers do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  bulkUpdateAdminVehicles,
  createAdminVehicle,
  deleteAdminVehicle,
  updateAdminVehicle,
} from '@dfq/shared/api/adminVehicles';
import { ApiError } from '@dfq/shared/api/_envelope';
import type { Vehicle, VehicleWritePayload } from '@dfq/shared/types/vehicle';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export type AdminVehicleMutationStatus = 'idle' | 'pending' | 'success' | 'error';

export interface AdminVehicleState {
  selectedIds: number[];
  bulkReassignOpen: boolean;
  mutationStatus: AdminVehicleMutationStatus;
  mutationError: ApiError | null;
}

const initialState: AdminVehicleState = {
  selectedIds: [],
  bulkReassignOpen: false,
  mutationStatus: 'idle',
  mutationError: null,
};

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * Generic shape for a thunk error payload — lets reducers consume the
 * already-typed {@link ApiError} via `action.payload` (rather than
 * `action.error.message`, which we never want to surface in the UI).
 */
type ThunkApiError = { rejectValue: ApiError };

export interface CreateVehicleArg {
  payload: VehicleWritePayload;
}

export const createVehicleThunk = createAsyncThunk<
  Vehicle,
  CreateVehicleArg,
  ThunkApiError
>('adminVehicles/create', async ({ payload }, { rejectWithValue }) => {
  try {
    return await createAdminVehicle(payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface UpdateVehicleArg {
  id: number;
  payload: Partial<VehicleWritePayload>;
}

export const updateVehicleThunk = createAsyncThunk<
  Vehicle,
  UpdateVehicleArg,
  ThunkApiError
>('adminVehicles/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await updateAdminVehicle(id, payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface DeleteVehicleArg {
  id: number;
}

export const deleteVehicleThunk = createAsyncThunk<
  number,
  DeleteVehicleArg,
  ThunkApiError
>('adminVehicles/delete', async ({ id }, { rejectWithValue }) => {
  try {
    await deleteAdminVehicle(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface BulkUpdateVehiclesArg {
  ids: number[];
  patch: Partial<VehicleWritePayload>;
}

export const bulkUpdateVehiclesThunk = createAsyncThunk<
  Vehicle[],
  BulkUpdateVehiclesArg,
  ThunkApiError
>('adminVehicles/bulkUpdate', async ({ ids, patch }, { rejectWithValue }) => {
  try {
    return await bulkUpdateAdminVehicles({ ids, patch });
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

export const adminVehicleSlice = createSlice({
  name: 'adminVehicles',
  initialState,
  reducers: {
    setSelectedIds(state, action: PayloadAction<number[]>) {
      state.selectedIds = action.payload;
    },
    clearSelection(state) {
      state.selectedIds = [];
    },
    openBulkReassign(state) {
      state.bulkReassignOpen = true;
    },
    closeBulkReassign(state) {
      state.bulkReassignOpen = false;
    },
    resetMutation(state) {
      state.mutationStatus = 'idle';
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    const setPending = (state: AdminVehicleState) => {
      state.mutationStatus = 'pending';
      state.mutationError = null;
    };
    const setSuccess = (state: AdminVehicleState) => {
      state.mutationStatus = 'success';
      state.mutationError = null;
    };
    const setError = (
      state: AdminVehicleState,
      action: { payload?: ApiError; error?: { message?: string } },
    ) => {
      state.mutationStatus = 'error';
      state.mutationError =
        action.payload instanceof ApiError
          ? action.payload
          : new ApiError({
              message: action.error?.message ?? 'Vehicle save failed',
              status: 0,
            });
    };

    builder
      .addCase(createVehicleThunk.pending, setPending)
      .addCase(createVehicleThunk.fulfilled, setSuccess)
      .addCase(createVehicleThunk.rejected, setError)

      .addCase(updateVehicleThunk.pending, setPending)
      .addCase(updateVehicleThunk.fulfilled, setSuccess)
      .addCase(updateVehicleThunk.rejected, setError)

      .addCase(deleteVehicleThunk.pending, setPending)
      .addCase(deleteVehicleThunk.fulfilled, setSuccess)
      .addCase(deleteVehicleThunk.rejected, setError)

      .addCase(bulkUpdateVehiclesThunk.pending, setPending)
      .addCase(bulkUpdateVehiclesThunk.fulfilled, (state) => {
        state.mutationStatus = 'success';
        state.mutationError = null;
        state.selectedIds = [];
        state.bulkReassignOpen = false;
      })
      .addCase(bulkUpdateVehiclesThunk.rejected, setError);
  },
});

export const {
  setSelectedIds,
  clearSelection,
  openBulkReassign,
  closeBulkReassign,
  resetMutation,
} = adminVehicleSlice.actions;

export const adminVehicleReducer = adminVehicleSlice.reducer;

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

export interface AdminVehicleRootStateSlice {
  adminVehicles: AdminVehicleState;
}

export const selectAdminVehicleSelectedIds = (
  state: AdminVehicleRootStateSlice,
): number[] => state.adminVehicles.selectedIds;

export const selectAdminVehicleBulkReassignOpen = (
  state: AdminVehicleRootStateSlice,
): boolean => state.adminVehicles.bulkReassignOpen;

export const selectAdminVehicleMutation = (
  state: AdminVehicleRootStateSlice,
): { status: AdminVehicleMutationStatus; error: ApiError | null } => ({
  status: state.adminVehicles.mutationStatus,
  error: state.adminVehicles.mutationError,
});
